import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Phone, MapPin, Send } from "lucide-react";
import { PHONE, PHONE_TEL, waLink } from "../../data";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Inserisci nome e numero di telefono.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
    } catch (_) {
      /* non-blocking: apriamo comunque WhatsApp */
    }
    setLoading(false);
    const text = `Salve ELETTRIKPRO, sono ${form.name}. ${
      form.message || "Ho bisogno di un intervento elettrico."
    } Il mio numero è ${form.phone}.`;
    toast.success("Ti reindirizziamo su WhatsApp...");
    window.open(waLink(text), "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="contatti"
      className="relative overflow-hidden py-24 sm:py-32"
      data-testid="contact-section"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-[#FFC400]/10 blur-[120px]" />

      <div className="relative mx-auto max-w-6xl px-5">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#FFC400]">
              / Contatti
            </p>
            <h2 className="font-display text-3xl font-extrabold tracking-tighter text-white sm:text-5xl">
              Hai un problema elettrico? Contattaci subito.
            </h2>
            <p className="mt-4 text-white/70">
              Interventi rapidi a Napoli e provincia, disponibili 24 ore su 24.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {/* left: contact info + map */}
          <Reveal>
            <div className="flex h-full flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-card"
                  className="group rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-[#FFC400]/50"
                >
                  <MessageCircle className="mb-3 h-7 w-7 text-[#FFC400]" />
                  <p className="text-xs uppercase tracking-widest text-white/50">
                    WhatsApp
                  </p>
                  <p className="font-display text-lg font-bold text-white">
                    {PHONE}
                  </p>
                </a>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="contact-call-card"
                  className="group rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-[#FFC400]/50"
                >
                  <Phone className="mb-3 h-7 w-7 text-[#FFC400]" />
                  <p className="text-xs uppercase tracking-widest text-white/50">
                    Telefono
                  </p>
                  <p className="font-display text-lg font-bold text-white">
                    388 836 3831
                  </p>
                </a>
              </div>

              <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <MapPin className="h-7 w-7 shrink-0 text-[#FFC400]" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/50">
                    Zona
                  </p>
                  <p className="font-semibold text-white">
                    San Giorgio a Cremano · Napoli e provincia
                  </p>
                </div>
              </div>

              <div className="flex-1 overflow-hidden rounded-2xl border border-white/10">
                <iframe
                  title="Mappa ELETTRIKPRO San Giorgio a Cremano"
                  src="https://www.google.com/maps?q=San%20Giorgio%20a%20Cremano%2C%20Napoli&output=embed"
                  className="map-dark h-full min-h-[240px] w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>

          {/* right: form */}
          <Reveal delay={0.1}>
            <form
              onSubmit={handleSubmit}
              className="glass rounded-3xl p-6 sm:p-8"
              data-testid="contact-form"
            >
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-white/70">
                    Nome
                  </label>
                  <input
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Il tuo nome"
                    data-testid="form-name-input"
                    className="w-full rounded-xl border border-white/10 bg-[#020813]/60 px-4 py-3 text-white placeholder-white/30 outline-none transition-colors focus:border-[#FFC400]"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-white/70">
                    Telefono
                  </label>
                  <input
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Il tuo numero"
                    data-testid="form-phone-input"
                    className="w-full rounded-xl border border-white/10 bg-[#020813]/60 px-4 py-3 text-white placeholder-white/30 outline-none transition-colors focus:border-[#FFC400]"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-white/70">
                    Messaggio
                  </label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Descrivi il tuo problema elettrico..."
                    data-testid="form-message-input"
                    className="w-full resize-none rounded-xl border border-white/10 bg-[#020813]/60 px-4 py-3 text-white placeholder-white/30 outline-none transition-colors focus:border-[#FFC400]"
                  />
                </div>
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: 0.98 }}
                  data-testid="form-submit-btn"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FFC400] px-6 py-4 font-bold text-[#0B1F3A] transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60 glow-yellow"
                >
                  {loading ? (
                    "Invio..."
                  ) : (
                    <>
                      <Send className="h-5 w-5" strokeWidth={2.5} /> Richiedi
                      intervento su WhatsApp
                    </>
                  )}
                </motion.button>
                <p className="text-center text-xs text-white/40">
                  Inviando accetti di essere ricontattato per l'intervento
                  richiesto.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
